"use client";

import { useState, useCallback, useEffect, useRef, type MouseEvent } from "react";
import { PUBLIC_HEADER_OFFSET } from "@/app/(public)/[market]/_components/landing-snap-section";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { PUBLIC_CTA_PRIMARY_CLASSES } from "@/components/landing/public-cta";
import { Menu, ArrowRight, X, ChevronDown } from "lucide-react";
import { BOOK_DEMO_URL } from "@/config/booking";
import { scrollToElement } from "@/components/landing/scroll-to-element";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const INTEGRATION_LINKS = [
  { href: "/integrations/simpro", label: "Simpro" },
  { href: "/integrations/servicem8", label: "ServiceM8" },
  { href: "/integrations/tradify", label: "Tradify" },
  { href: "/integrations/fergus", label: "Fergus" },
];

const CAPABILITY_LINKS = [
  { href: "/follow-up-automation", label: "Follow-up automation" },
  { href: "/lost-reactivation", label: "Lost lead reactivation" },
  { href: "/google-reviews", label: "Google review system" },
  { href: "/contact-forms", label: "Contact forms" },
  { href: "/analytics", label: "Analytics" },
];

function NavDropdown({ label, links }: { label: string; links: { href: string; label: string }[] }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on navigation so the menu doesn't follow the user to the next page
  // (pointer/focus can still be inside the old header when the new page mounts).
  useEffect(() => {
    setOpen(false);
    setKeyboardFocus(false);
  }, [pathname]);

  // Click-toggle opens a sticky menu that survives hover-out; Escape and
  // outside clicks close it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setKeyboardFocus(false);
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const isOpen = open || keyboardFocus;

  return (
    <div
      ref={rootRef}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setKeyboardFocus(true)}
        onBlur={(e) => {
          // Keep the menu open while focus moves into it; only clear when
          // focus leaves the whole dropdown container.
          if (!rootRef.current?.contains(e.relatedTarget as Node | null)) {
            setKeyboardFocus(false);
          }
        }}
        className="rounded-sm px-2 py-1 text-sm font-semibold font-mono text-canvas-muted outline-none transition-colors hover:text-canvas-fg focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {label}
        <ChevronDown className="ml-1 inline h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {/* pt-2 bridges the gap so hover survives moving from button to menu */}
      <div
        className={`invisible absolute left-0 top-full z-50 pt-2 opacity-0 transition-opacity duration-150 ${
          isOpen ? "visible opacity-100" : ""
        }`}
      >
        <div className="min-w-[13rem] border border-canvas-border bg-canvas-surface shadow-lg">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => {
                setOpen(false);
                setKeyboardFocus(false);
              }}
              className="block px-4 py-2.5 font-mono text-sm text-canvas-fg-mid transition-colors hover:bg-canvas-hover hover:text-canvas-fg focus-visible:bg-canvas-hover focus-visible:text-canvas-fg focus-visible:outline-none"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Wordmark() {
  return (
    <span className="inline-flex select-none items-center gap-2 font-[family-name:var(--font-bebas-neue)] text-[15px] font-bold uppercase tracking-[0.22em] text-canvas-fg">
      <Image
        src="/logos/mark.svg"
        alt=""
        aria-hidden="true"
        width={17}
        height={15}
        className="h-[15px] w-auto"
      />
      Strelve
    </span>
  );
}

const DESKTOP_MIN_WIDTH = 1200;

export function PublicHeader() {
  const primaryActionHref = BOOK_DEMO_URL;
  const primaryActionLabel = "BOOK A DEMO";
  const signInHref = "/login";
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const updateScrolled = () => setScrolled(window.scrollY > 50);
    updateScrolled();
    window.addEventListener("scroll", updateScrolled, { passive: true });
    return () => window.removeEventListener("scroll", updateScrolled);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    const nav = navRef.current;
    const actions = actionsRef.current;
    if (!el || !nav || !actions) return;

    const checkOverflow = () => {
      const containerWidth = el.clientWidth;
      // Space needed: logo (absolute left) + nav + gap + actions + padding
      const navWidth = nav.scrollWidth;
      const actionsWidth = actions.scrollWidth;
      // 48px = left padding (logo area) + 12px gap + right padding
      // Add 80px buffer so menu triggers before items get cramped
      const requiredWidth = navWidth + actionsWidth + 48 + 80;
      const isOverflowing = requiredWidth > containerWidth;
      const isNarrowViewport = containerWidth < DESKTOP_MIN_WIDTH;
      setIsMobile(isOverflowing || isNarrowViewport);
    };

    // Initial check
    checkOverflow();

    // Observe container for width changes
    const ro = new ResizeObserver(() => {
      checkOverflow();
    });
    ro.observe(el);

    // Also observe nav and actions for content changes (e.g., font loading)
    const navRo = new ResizeObserver(() => checkOverflow());
    navRo.observe(nav);
    const actionsRo = new ResizeObserver(() => checkOverflow());
    actionsRo.observe(actions);

    return () => {
      ro.disconnect();
      navRo.disconnect();
      actionsRo.disconnect();
    };
  }, []);

  const handleLogoClick = useCallback((e: MouseEvent) => {
    if (window.location.pathname === "/") {
      e.preventDefault();
      window.history.replaceState(null, "", "/");
      window.scrollTo({
        top: 0,
        behavior:
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
      });
    }
  }, []);

  const handleDemoClick = useCallback((e: MouseEvent) => {
    if (window.location.pathname !== "/") return;
    e.preventDefault();
    setTimeout(() => {
      scrollToElement("demo", PUBLIC_HEADER_OFFSET);
    }, 150);
  }, []);

  const handleAnchorClick = useCallback((e: MouseEvent, id: string) => {
    if (window.location.pathname !== "/") return;
    e.preventDefault();
    setTimeout(() => {
      scrollToElement(id, PUBLIC_HEADER_OFFSET);
    }, 150);
  }, []);

  return (
    <>
      <nav
        aria-label="Main"
        className={`fixed inset-x-0 top-0 z-50 landing-px bg-canvas-surface transition-all duration-500 ${
          scrolled
            ? "border-b border-canvas-border shadow-sm pt-2.5 pb-2.5"
            : "border-b border-transparent pt-3 pb-3"
        }`}
      >
        <div ref={headerRef} className="relative mx-auto flex w-full min-w-0 max-w-[1520px] items-center justify-center gap-3">
          <Link
            href="/"
            onClick={handleLogoClick}
            className="group absolute left-0 inline-flex min-h-[44px] shrink-0 items-center transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm"
          >
            <Wordmark />
          </Link>

          <div ref={navRef} className={`${isMobile ? "hidden" : "flex"} items-center gap-4 pl-3`}>
            <Link
              href="/#demo"
              onClick={handleDemoClick}
              className="text-sm font-semibold font-mono text-canvas-muted transition-colors hover:text-canvas-fg focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm px-2 py-1 min-h-[44px] inline-flex items-center"
            >
              Demo
            </Link>
            <Link
              href="/#how-it-works"
              onClick={(e) => handleAnchorClick(e, "how-it-works")}
              className="text-sm font-semibold font-mono text-canvas-muted transition-colors hover:text-canvas-fg focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm px-2 py-1 min-h-[44px] inline-flex items-center"
            >
              How it works
            </Link>
            <Link
              href="/#faq"
              onClick={(e) => handleAnchorClick(e, "faq")}
              className="text-sm font-semibold font-mono text-canvas-muted transition-colors hover:text-canvas-fg focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm px-2 py-1 min-h-[44px] inline-flex items-center"
            >
              FAQ
            </Link>
            <NavDropdown label="Integrations" links={INTEGRATION_LINKS} />
            <NavDropdown label="Capabilities" links={CAPABILITY_LINKS} />
            <Link
              href="/pricing"
              onClick={(e) => {
                if (window.location.pathname === "/pricing") {
                  e.preventDefault();
                  window.scrollTo({
                    top: 0,
                    behavior:
                      typeof window.matchMedia === "function" &&
                      window.matchMedia("(prefers-reduced-motion: reduce)").matches
                        ? "auto"
                        : "smooth",
                  });
                }
              }}
              className="text-sm font-semibold font-mono text-canvas-muted transition-colors hover:text-canvas-fg focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm px-2 py-1 min-h-[44px] inline-flex items-center"
            >
              Pricing
            </Link>
          </div>

          <div ref={actionsRef} className={`absolute right-0 ${isMobile ? "hidden" : "flex"} items-center gap-3`}>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-11 rounded-none px-5 font-mono text-sm font-bold text-canvas-muted hover:text-canvas-fg sm:h-10 focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
            >
              <Link href={signInHref}>SIGN IN</Link>
            </Button>
            <Button
              asChild
              size="sm"
              className={`${PUBLIC_CTA_PRIMARY_CLASSES} h-12 rounded-none px-6`}
            >
              <Link href={primaryActionHref} target="_blank" rel="noopener noreferrer">
                {primaryActionLabel}
              </Link>
            </Button>
          </div>

          <div className={`ml-auto flex shrink-0 items-center ${isMobile ? "flex" : "hidden"}`}>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open menu"
                  className="rounded-full text-canvas-muted hover:bg-canvas-hover size-11 flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                >
                  <Menu className="h-6 w-6" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                showCloseButton={false}
                className="marketing-paper flex w-[100vw] sm:max-w-[24rem] flex-col border-l border-canvas-border bg-canvas/95 p-0 backdrop-blur-xl"
              >
                <SheetHeader className="shrink-0 border-b border-canvas-border bg-canvas-inset p-5 text-left sm:p-6">
                  <div className="flex items-center justify-between gap-4">
                    <SheetTitle>
                      <Wordmark />
                    </SheetTitle>
                    <SheetClose
                      aria-label="Close menu"
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-canvas-fg hover:bg-canvas-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <X className="h-6 w-6" />
                    </SheetClose>
                  </div>
                </SheetHeader>
                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-5 pb-8 sm:p-6 sm:pb-10">
                  <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted">
                    Navigation
                  </p>
                  <Link
                    href="/#demo"
                    onClick={(e) => {
                      setMobileMenuOpen(false);
                      handleDemoClick(e);
                    }}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Demo
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/#how-it-works"
                    onClick={(e) => {
                      setMobileMenuOpen(false);
                      handleAnchorClick(e, "how-it-works");
                    }}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    How it works
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/pricing"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (window.location.pathname === "/pricing") {
                        window.scrollTo({
                          top: 0,
                          behavior:
                            typeof window.matchMedia === "function" &&
                            window.matchMedia("(prefers-reduced-motion: reduce)").matches
                              ? "auto"
                              : "smooth",
                        });
                      }
                    }}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Pricing
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/follow-up-automation"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Follow-up automation
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/lost-reactivation"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Lost lead reactivation
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/google-reviews"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Google review system
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/contact-forms"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Contact forms
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/analytics"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Analytics
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/integrations/simpro"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Simpro integration
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/integrations/servicem8"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    ServiceM8 integration
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/integrations/tradify"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Tradify integration
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/integrations/fergus"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    Fergus integration
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                  <Link
                    href="/#faq"
                    onClick={(e) => {
                      setMobileMenuOpen(false);
                      handleAnchorClick(e, "faq");
                    }}
                    className="group flex min-h-[48px] items-center rounded-xl px-4 py-3 font-mono font-bold text-canvas-fg-mid transition-colors hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                  >
                    FAQ
                    <ArrowRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>

                  <div className="mt-8 flex flex-col gap-4 border-t border-canvas-border pt-8">
                    <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted">
                      Account
                    </p>
                    <Button
                      asChild
                      variant="outline"
                      className="h-12 w-full justify-center rounded-none border-canvas-border-mid bg-canvas-inset font-mono font-bold text-canvas-fg hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
                    >
                      <Link href={signInHref}>SIGN IN</Link>
                    </Button>
                    <Button
                      asChild
                      className={`${PUBLIC_CTA_PRIMARY_CLASSES} h-12 w-full rounded-none`}
                    >
                      <Link href={primaryActionHref} target="_blank" rel="noopener noreferrer">
                        {primaryActionLabel}
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
}
